import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, Check, X } from 'lucide-react';
import {
  LmsAttemptAnswer,
  LmsAttemptStatus,
  LmsMcqMultiContent,
  LmsMcqSingleContent,
  LmsQuizAttemptRow,
  LmsQuizAttemptSnapshotQuestion,
  LmsQuizAttemptsApi,
  LmsQuizListItem,
  LmsShortAnswerContent,
  LmsTrueFalseContent,
} from '@/services/lms.api';
import { DataTable } from '@/components/ui/DataTable';
import { Pagination } from '@/components/ui/Pagination';
import { Modal } from '@/components/ui/Modal';
import { Badge } from '@/components/ui/Badge';
import { Field, Input } from '@/components/ui/Input';
import { formatDateTime } from '@/lib/format';
import { toast } from '@/stores/toast.store';

const statusTone: Record<LmsAttemptStatus, 'blue' | 'amber' | 'red' | 'green'> = {
  in_progress: 'blue',
  submitted: 'amber',
  expired: 'red',
  graded: 'green',
};

function errMsg(e: unknown): string | undefined {
  if (!e) return undefined;
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong';
}

export function LmsQuizAttemptsModal({ quiz, onClose }: { quiz: LmsQuizListItem; onClose: () => void }) {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [selected, setSelected] = useState<LmsQuizAttemptRow | null>(null);

  const { data: pageData, isLoading } = useQuery({
    queryKey: ['lms-attempts', quiz.id, page, limit],
    queryFn: () => LmsQuizAttemptsApi.list(quiz.id, { page, limit }),
    placeholderData: keepPreviousData,
  });

  const rows = pageData?.items ?? [];

  return (
    <Modal
      open
      onClose={onClose}
      title={selected ? `Attempt — ${selected.studentName ?? 'Unknown student'}` : `Attempts — ${quiz.title}`}
      size="xl"
    >
      {selected ? (
        <AttemptDetail quizId={quiz.id} row={selected} onBack={() => setSelected(null)} />
      ) : (
        <div>
          <DataTable<LmsQuizAttemptRow>
            rows={rows}
            getRowId={(r) => r.id}
            isLoading={isLoading}
            emptyMessage="No attempts yet."
            columns={[
              {
                key: 'student',
                header: 'Student',
                render: (r) => (
                  <div>
                    <div className="font-medium text-slate-900">{r.studentName ?? '—'}</div>
                    <div className="text-xs text-slate-400">{r.admissionNumber ?? '—'}</div>
                  </div>
                ),
              },
              { key: 'attemptNumber', header: '#', render: (r) => r.attemptNumber },
              { key: 'status', header: 'Status', render: (r) => <Badge tone={statusTone[r.status]}>{r.status}</Badge> },
              {
                key: 'score',
                header: 'Score',
                render: (r) => (r.score != null ? `${r.score}/${r.maxScore}` : '—'),
              },
              {
                key: 'passed',
                header: 'Passed',
                render: (r) =>
                  r.passed == null ? (
                    <span className="text-slate-300">—</span>
                  ) : r.passed ? (
                    <Check className="h-4 w-4 text-green-600" />
                  ) : (
                    <X className="h-4 w-4 text-red-600" />
                  ),
              },
              {
                key: 'pending',
                header: 'Pending',
                render: (r) =>
                  r.pendingManualCount > 0 ? (
                    <Badge tone="amber">{r.pendingManualCount} pending</Badge>
                  ) : (
                    <span className="text-slate-300">—</span>
                  ),
              },
              { key: 'startedAt', header: 'Started', render: (r) => formatDateTime(r.startedAt) },
              { key: 'submittedAt', header: 'Submitted', render: (r) => formatDateTime(r.submittedAt) },
            ]}
            actions={(r) => (
              <button className="btn-secondary" onClick={() => setSelected(r)}>
                View
              </button>
            )}
          />

          {pageData && (
            <Pagination
              page={pageData.page}
              totalPages={pageData.totalPages}
              total={pageData.total}
              limit={pageData.limit}
              onPageChange={setPage}
              onLimitChange={(l) => {
                setPage(1);
                setLimit(l);
              }}
            />
          )}
        </div>
      )}
    </Modal>
  );
}

function AttemptDetail({
  quizId,
  row,
  onBack,
}: {
  quizId: string;
  row: LmsQuizAttemptRow;
  onBack: () => void;
}) {
  const qc = useQueryClient();
  const { data: attempt, isLoading } = useQuery({
    queryKey: ['lms-attempt-detail', row.id],
    queryFn: () => LmsQuizAttemptsApi.get(row.id),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['lms-attempt-detail', row.id] });
    qc.invalidateQueries({ queryKey: ['lms-attempts', quizId] });
  };

  if (isLoading || !attempt) {
    return (
      <div>
        <BackButton onBack={onBack} />
        <div className="py-8 text-center text-slate-400">Loading…</div>
      </div>
    );
  }

  const answerByQuestion = new Map(attempt.answers.map((a) => [a.questionId, a]));
  const questions = [...attempt.questionsSnapshot].sort((a, b) => a.displayOrder - b.displayOrder);

  return (
    <div className="space-y-5">
      <BackButton onBack={onBack} />

      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={statusTone[attempt.status]}>{attempt.status}</Badge>
        <span className="text-sm text-slate-700">
          Score: {attempt.score ?? '—'}/{attempt.maxScore}
        </span>
        <span className="text-sm text-slate-700">
          Passed:{' '}
          {attempt.passed == null ? (
            '—'
          ) : attempt.passed ? (
            <Check className="inline h-4 w-4 text-green-600" />
          ) : (
            <X className="inline h-4 w-4 text-red-600" />
          )}
        </span>
        <span className="text-xs text-slate-400">Started {formatDateTime(attempt.startedAt)}</span>
        {attempt.submittedAt && (
          <span className="text-xs text-slate-400">· Submitted {formatDateTime(attempt.submittedAt)}</span>
        )}
      </div>

      <div className="space-y-4">
        {questions.map((q, i) => (
          <QuestionAnswerCard
            key={q.questionId}
            index={i + 1}
            question={q}
            answer={answerByQuestion.get(q.questionId)}
            attemptId={attempt.id}
            attemptStatus={attempt.status}
            onGraded={invalidate}
          />
        ))}
      </div>
    </div>
  );
}

function BackButton({ onBack }: { onBack: () => void }) {
  return (
    <button className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800" onClick={onBack}>
      <ArrowLeft className="h-4 w-4" /> Back to attempts
    </button>
  );
}

function QuestionAnswerCard({
  index,
  question,
  answer,
  attemptId,
  attemptStatus,
  onGraded,
}: {
  index: number;
  question: LmsQuizAttemptSnapshotQuestion;
  answer: LmsAttemptAnswer | undefined;
  attemptId: string;
  attemptStatus: LmsAttemptStatus;
  onGraded: () => void;
}) {
  return (
    <div className="rounded-lg border border-slate-200 p-4">
      <div className="mb-2 flex items-start justify-between gap-2">
        <p className="text-sm font-medium text-slate-800">
          {index}. {question.prompt}
        </p>
        <span className="shrink-0 text-xs text-slate-400">{question.marks} mark{question.marks === 1 ? '' : 's'}</span>
      </div>

      {question.type === 'mcq_single' && (
        <McqSingleAnswer content={question.content as unknown as LmsMcqSingleContent} answer={answer} />
      )}
      {question.type === 'mcq_multi' && (
        <McqMultiAnswer content={question.content as unknown as LmsMcqMultiContent} answer={answer} />
      )}
      {question.type === 'true_false' && (
        <TrueFalseAnswer content={question.content as unknown as LmsTrueFalseContent} answer={answer} />
      )}
      {question.type === 'short_answer' && (
        <ShortAnswerCard
          content={question.content as unknown as LmsShortAnswerContent}
          maxMarks={question.marks}
          answer={answer}
          questionId={question.questionId}
          attemptStatus={attemptStatus}
          onGraded={onGraded}
          attemptId={attemptId}
        />
      )}
    </div>
  );
}

function McqSingleAnswer({ content, answer }: { content: LmsMcqSingleContent; answer?: LmsAttemptAnswer }) {
  const selected = (answer?.answer as { selectedOptionId?: string } | undefined)?.selectedOptionId;
  return (
    <ul className="space-y-1 text-sm">
      {content.options.map((o) => {
        const isSelected = selected === o.id;
        const isCorrectOpt = content.correctOptionId === o.id;
        return (
          <li
            key={o.id}
            className={
              isCorrectOpt
                ? 'rounded-md bg-green-50 px-2 py-1 text-green-800'
                : isSelected
                  ? 'rounded-md bg-red-50 px-2 py-1 text-red-800'
                  : 'px-2 py-1 text-slate-600'
            }
          >
            {isSelected && '→ '}
            {o.text}
            {isCorrectOpt && ' (correct)'}
          </li>
        );
      })}
      {!selected && <li className="px-2 py-1 text-xs italic text-slate-400">No answer submitted</li>}
    </ul>
  );
}

function McqMultiAnswer({ content, answer }: { content: LmsMcqMultiContent; answer?: LmsAttemptAnswer }) {
  const selected = new Set(
    (answer?.answer as { selectedOptionIds?: string[] } | undefined)?.selectedOptionIds ?? [],
  );
  const correct = new Set(content.correctOptionIds);
  return (
    <ul className="space-y-1 text-sm">
      {content.options.map((o) => {
        const isSelected = selected.has(o.id);
        const isCorrectOpt = correct.has(o.id);
        return (
          <li
            key={o.id}
            className={
              isCorrectOpt
                ? 'rounded-md bg-green-50 px-2 py-1 text-green-800'
                : isSelected
                  ? 'rounded-md bg-red-50 px-2 py-1 text-red-800'
                  : 'px-2 py-1 text-slate-600'
            }
          >
            {isSelected && '→ '}
            {o.text}
            {isCorrectOpt && ' (correct)'}
          </li>
        );
      })}
    </ul>
  );
}

function TrueFalseAnswer({ content, answer }: { content: LmsTrueFalseContent; answer?: LmsAttemptAnswer }) {
  const selected = (answer?.answer as { answer?: boolean } | undefined)?.answer;
  return (
    <p className="text-sm text-slate-700">
      Student answered:{' '}
      <span className="font-medium">{selected == null ? 'No answer' : selected ? 'True' : 'False'}</span>
      {' · '}Correct: <span className="font-medium text-green-700">{content.correctAnswer ? 'True' : 'False'}</span>
    </p>
  );
}

const gradeSchema = (maxMarks: number) =>
  z.object({
    marksAwarded: z
      .string()
      .min(1, 'Required')
      .refine(
        (v) => {
          const n = Number(v);
          return Number.isFinite(n) && n >= 0 && n <= maxMarks;
        },
        { message: `Must be between 0 and ${maxMarks}` },
      ),
  });

function ShortAnswerCard({
  content,
  maxMarks,
  answer,
  questionId,
  attemptStatus,
  attemptId,
  onGraded,
}: {
  content: LmsShortAnswerContent;
  maxMarks: number;
  answer: LmsAttemptAnswer | undefined;
  questionId: string;
  attemptStatus: LmsAttemptStatus;
  attemptId: string;
  onGraded: () => void;
}) {
  const text = (answer?.answer as { text?: string } | undefined)?.text;
  const schema = gradeSchema(maxMarks);
  type FormValues = z.infer<typeof schema>;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    values: { marksAwarded: answer?.marksAwarded != null ? String(answer.marksAwarded) : '' },
  });

  const grade = useMutation({
    mutationFn: (v: FormValues) =>
      LmsQuizAttemptsApi.grade(attemptId, questionId, { marksAwarded: Number(v.marksAwarded) }),
    onSuccess: () => {
      onGraded();
      toast.success('Grade saved');
    },
    onError: (e) => toast.error(errMsg(e) ?? 'Grading failed'),
  });

  const gradable = attemptStatus !== 'in_progress';

  return (
    <div className="space-y-2 text-sm">
      <p className="text-slate-700">
        <span className="text-slate-400">Answer:</span> {text?.trim() ? text : <em className="text-slate-400">No answer submitted</em>}
      </p>
      {content.expectedAnswer && (
        <p className="text-slate-500">
          <span className="text-slate-400">Expected:</span> {content.expectedAnswer}
          {content.caseSensitive ? ' (case-sensitive)' : ''}
        </p>
      )}

      {gradable ? (
        <form className="flex items-end gap-2" onSubmit={handleSubmit((v) => grade.mutate(v))}>
          <Field label={`Marks (0–${maxMarks})`} error={errors.marksAwarded?.message}>
            <Input type="number" min={0} max={maxMarks} className="w-28" {...register('marksAwarded')} />
          </Field>
          <button type="submit" className="btn-primary" disabled={grade.isPending}>
            {grade.isPending ? 'Saving…' : answer?.marksAwarded != null ? 'Regrade' : 'Grade'}
          </button>
        </form>
      ) : (
        <p className="text-xs italic text-slate-400">Attempt still in progress — not gradable yet.</p>
      )}
    </div>
  );
}
